import { useCallback } from 'react';
import { useOrganization } from '@/lib/OrganizationContext';
import { useSession } from '@/lib/AuthContext';
import { useOrgStore } from '@/stores/orgStore';
import { hasDefaultNavigationAccess } from '@/lib/navigation/buildNavigationStructure';
import type { NavigationPermission } from '@/types/navigation';
import { useOrganizationNavigation } from '@/hooks/api/useNavigationConfig';
import { useRolePermissionsMutations } from '@/hooks/api/useRolePermissions';

export function useRolePermissions(orgId?: string) {
  const { currentOrganization } = useOrganization();
  const { session } = useSession();
  const storeOrgId = useOrgStore((state) => state.currentOrganizationId);
  const resolvedOrgId = orgId ?? currentOrganization?.id ?? storeOrgId ?? undefined;

  const {
    navigationConfig,
    navigationConfigByCategories,
    rolePermissions: customPermissions,
    isLoading,
  } = useOrganizationNavigation(resolvedOrgId);
  const { upsertPermissionMutation, updateMultiplePermissionsMutation, resetPermissionsMutation } =
    useRolePermissionsMutations(resolvedOrgId);

  const hasDefaultAccess = useCallback(
    (path: string, role: string) => hasDefaultNavigationAccess(navigationConfig, path, role),
    [navigationConfig],
  );

  const getEffectivePermissions = useCallback(
    (role: string): NavigationPermission[] => {
      if (role === 'owner') {
        return navigationConfig;
      }

      const customPermByKey = new Map(
        customPermissions.map((p) => [`${p.role}-${p.navigation_path}`, p]),
      );

      return navigationConfig
        .map((item) => {
          const filteredSubItems = item.subItems
            ? item.subItems.filter((subItem) => {
                const customSubPerm = customPermByKey.get(`${role}-${subItem.path}`);
                return customSubPerm ? customSubPerm.has_access : subItem.roles.includes(role);
              })
            : undefined;

          return {
            ...item,
            subItems: filteredSubItems,
          };
        })
        .filter((item) => {
          if (item.subItems) {
            return item.subItems.length > 0;
          }

          const customPerm = customPermByKey.get(`${role}-${item.path}`);
          return customPerm ? customPerm.has_access : item.roles.includes(role);
        });
    },
    [customPermissions, navigationConfig],
  );

  const checkAccess = useCallback(
    (role: string, navigationPath: string): boolean => {
      if (role === 'owner') return true;

      const customPerm = customPermissions.find(
        (permission) => permission.role === role && permission.navigation_path === navigationPath,
      );

      if (customPerm) {
        return customPerm.has_access;
      }

      return hasDefaultAccess(navigationPath, role);
    },
    [customPermissions, hasDefaultAccess],
  );

  const updatePermission = useCallback(
    async (
      role: 'admin' | 'member' | 'coach' | 'player',
      navigationPath: string,
      hasAccess: boolean,
    ) => {
      if (!resolvedOrgId || !session?.user?.id) {
        throw new Error('Organización o usuario no disponible');
      }

      await upsertPermissionMutation.mutateAsync({
        role,
        navigationPath,
        hasAccess,
        userId: session.user.id,
      });
    },
    [resolvedOrgId, session?.user?.id, upsertPermissionMutation],
  );

  const updateMultiplePermissions = useCallback(
    async (
      permissions: Array<{
        role: 'admin' | 'member' | 'coach' | 'player';
        navigationPath: string;
        hasAccess: boolean;
      }>,
    ) => {
      if (!resolvedOrgId || !session?.user?.id) {
        throw new Error('Organización o usuario no disponible');
      }

      await updateMultiplePermissionsMutation.mutateAsync({
        permissions,
        userId: session.user.id,
      });
    },
    [resolvedOrgId, session?.user?.id, updateMultiplePermissionsMutation],
  );

  const resetPermissions = useCallback(async () => {
    if (!resolvedOrgId) {
      throw new Error('Organización no disponible');
    }
    await resetPermissionsMutation.mutateAsync();
  }, [resolvedOrgId, resetPermissionsMutation]);

  const reloadPermissions = useCallback(async () => {
    // TanStack Query refetch is handled by invalidation in mutations.
  }, []);

  return {
    customPermissions,
    navigationConfigByCategories,
    isLoading,
    getEffectivePermissions,
    updatePermission,
    updateMultiplePermissions,
    resetPermissions,
    checkAccess,
    hasDefaultAccess,
    reloadPermissions,
  };
}
